import React, { useState, useEffect } from 'react';
import { BookCard } from '../components/BookCard';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { BookOpen, Bookmark, FolderOpen, ArrowLeft, User, Calendar, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../config/api';

export default function MyLibrary({ onSelectBook }) {
  const { user, token } = useAuth();
  const [selectedClass, setSelectedClass] = useState(null);
  const [classesWithReadings, setClassesWithReadings] = useState([]);
  const [savedBooks, setSavedBooks] = useState([]);
  const [savedBookIds, setSavedBookIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Fetch user's library with classes and assignments
  const fetchLibraryData = React.useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      console.log('[MyLibrary] Fetching library (authenticated)');

      const response = await fetch(apiUrl('/api/library/with-classes'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[MyLibrary] Loaded library data:', data);

        // Add color to each class for UI
        const classColors = [
          'bg-blue-500',
          'bg-green-500',
          'bg-purple-500',
          'bg-orange-500',
          'bg-pink-500',
          'bg-indigo-500'
        ];

        const classesWithColors = (data.classes || []).map((cls, index) => ({
          ...cls,
          color: classColors[index % classColors.length]
        }));

        setClassesWithReadings(classesWithColors);
        setSavedBooks(data.savedBooks || []);

        // Create a Set of saved book IDs for quick lookups
        const bookIds = new Set((data.savedBooks || []).map(b => b.id));
        setSavedBookIds(bookIds);
      } else {
        console.error('[MyLibrary] Failed to load library:', response.status);
        setClassesWithReadings([]);
        setSavedBooks([]);
      }
    } catch (error) {
      console.error('[MyLibrary] Error loading library:', error);
      setClassesWithReadings([]);
      setSavedBooks([]);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Fetch library data on mount
  useEffect(() => {
    fetchLibraryData();
  }, [fetchLibraryData]);

  // Refetch when window regains focus (user returns to tab/window)
  // This ensures book status is fresh after generation completes in background
  useEffect(() => {
    const handleFocus = () => {
      console.log('[MyLibrary] Window focused - refetching library data');
      fetchLibraryData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchLibraryData]);

  // Get selected class data
  const currentClass = classesWithReadings.find(c => c.id === selectedClass);

  // When a book is unsaved from this page, remove it from the local list immediately
  const handleSavedChanged = (bookId, isSaved) => {
    if (!isSaved) {
      setSavedBooks(prev => prev.filter(b => b.id !== bookId));
      setSavedBookIds(prev => {
        const next = new Set(prev);
        next.delete(bookId);
        return next;
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2 font-serif">My Library</h1>
        <p className="text-lg text-muted-foreground">
          Your assigned readings and saved books
        </p>
      </div>

      {/* Assigned Readings Section */}
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-semibold">Assigned readings</h2>
        </div>

        {/* Show class folders or class detail view */}
        {!selectedClass ? (
          // Class Folders View
          classesWithReadings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {classesWithReadings.map((classData) => (
                <Card
                  key={classData.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedClass(classData.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`${classData.color} p-3 rounded-lg`}>
                        <FolderOpen className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg mb-1 truncate">
                          {classData.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <User className="h-3 w-3" />
                          <span>{classData.teacher}</span>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <Badge variant="secondary">
                            {classData.readings.length} {classData.readings.length === 1 ? 'reading' : 'readings'}
                          </Badge>
                          {classData.readings.some(r => r.dueDate) && (
                            <span className="text-xs text-muted-foreground">
                              Due soon
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed rounded-lg">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
              <p className="text-muted-foreground">No assigned readings yet</p>
            </div>
          )
        ) : (
          // Class Detail View
          currentClass && (
            <div>
              {/* Back Button and Class Header */}
              <div className="mb-6">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedClass(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to all classes
                </Button>

                <div className="flex items-start gap-4">
                  <div className={`${currentClass.color} p-4 rounded-lg`}>
                    <FolderOpen className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1">{currentClass.name}</h3>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span>{currentClass.teacher}</span>
                      <span className="mx-2">•</span>
                      <span>{currentClass.readings.length} {currentClass.readings.length === 1 ? 'reading' : 'readings'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Readings in this class */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentClass.readings.map((reading) => (
                  <div key={reading.id} className="relative">
                    <BookCard
                      book={reading}
                      onSelectBook={onSelectBook}
                      isSaved={savedBookIds.has(reading.id)}
                      skipLibraryCheck={true}
                      showDownload={true}
                    />
                    {reading.dueDate && (
                      <div className="absolute top-2 right-2 z-10">
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due {new Date(reading.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      {!selectedClass && (
        <>
          <Separator className="mb-12" />

          {/* Saved Books Section */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Bookmark className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Saved books</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : savedBooks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {savedBooks.map((book) => (
                  <BookCard
                    key={book.id}
                    book={book}
                    onSelectBook={onSelectBook}
                    isSaved={true}
                    skipLibraryCheck={true}
                    onSavedChanged={handleSavedChanged}
                    showDownload={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed rounded-lg">
                <Bookmark className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
                <p className="text-muted-foreground">No saved books yet</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
